import { createContext, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark" | "lilac-light" | "lilac-dark";

type ThemeContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: "dark" | "light";
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "dg-theme";

const KNOWN_THEMES: Theme[] = ["light", "dark", "lilac-light", "lilac-dark"];

const META_COLORS: Record<Theme, string> = {
  light: "#fcfcfc",
  dark: "#333333",
  "lilac-light": "#faf7f4",
  "lilac-dark": "#100d15",
};

function getDefaultTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
}

function applyTheme(theme: Theme): "dark" | "light" {
  const isDark = theme === "dark" || theme === "lilac-dark";
  const isLilac = theme === "lilac-light" || theme === "lilac-dark";
  document.documentElement.classList.toggle("dark", isDark);
  if (isLilac) {
    document.documentElement.setAttribute("data-theme", "lilac");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", META_COLORS[theme]);
  return isDark ? "dark" : "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && KNOWN_THEMES.includes(stored as Theme)) {
        return stored as Theme;
      }
    } catch {
      /* storage may be unavailable */
    }
    return getDefaultTheme();
  });

  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() =>
    applyTheme(theme),
  );

  const setTheme = (next: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage may be unavailable */
    }
    setThemeState(next);
    setResolvedTheme(applyTheme(next));
  };

  // Re-apply on mount in case the pre-paint script state diverged
  useEffect(() => {
    setResolvedTheme(applyTheme(theme));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
